<template>
	<app-section>
		<app-title class="small mb-2" title="Basket" />
		<div class="flex flex-col gap-2">
			<div v-if="!items.length" class="italic">Add Items from the menu on the left</div>
			<div class="items-container">
				<div v-for="item in items" :key="item.id" class="flex items-center justify-between">
					<div class="flex flex-col">
						<div>{{ item.itemName }}</div>
						<p class="text-muted" v-if="item.isComplex">
							{{ item.variant.description }}: {{ item.variantItem?.itemName }}
						</p>
						<p class="text-muted" v-if="item.isComplex">
							<span class="mr-1">Toppings: </span>
							<span v-for="topping in item.toppingItems" :key="topping.id">
								{{ topping.itemName }},
							</span>
						</p>
					</div>
					<div class="flex gap-2">
						<span class="text-13">
							{{
								$filters.formatCurrency(
									item.variantItem ? item.variantItem.price : item.itemPrice,
									restaurantCurrency
								)
							}}
						</span>
						<div class="border-p-light border-1 rounded-lg flex items-center h-6">
							<app-icon
								@click="$emit('removeFromBasket', item)"
								class="cursor-pointer border-p-light text-success"
								icon="minus"
							/>
							<div class="flex px-2 border-l-1 border-r-1 border-p-light w-10 justify-center">
								{{ item.quantity }}
							</div>
							<app-icon
								@click="item.quantity += 1"
								class="cursor-pointer border-p-light text-success"
								icon="plus"
							/>
						</div>
					</div>
				</div>
			</div>
			<div v-if="subtotal" class="flex flex-col items-end mr-2">
				<div class="flex">
					<span>Subtotal</span>
					<span class="w-32 text-right">{{ $filters.formatCurrency(subtotal, restaurantCurrency) }}</span>
				</div>
				<div class="flex">
					<span>Transport</span>
					<div class="flex w-32 justify-end">
						<span>{{ $filters.formatCurrency(transportCost, restaurantCurrency) }} </span>
					</div>
				</div>
				<div v-if="localServiceFee" class="flex">
					<span>Service Fee</span>
					<span class="w-32 text-right">{{
						$filters.formatCurrency(localServiceFee, restaurantCurrency)
					}}</span>
				</div>
				<div v-if="discountValue" class="flex">
					<span>Discount</span>
					<span class="w-32 text-right">
						<span class="bg-success text-white px-2 py-px rounded-sm mr-3">
							<app-icon icon="gift" class="text-white" />
						</span>
						<span>{{ $filters.formatCurrency(discountValue, restaurantCurrency) }}</span>
					</span>
				</div>
				<div class="flex">
					<span>Total</span>
					<span class="w-32 text-right font-semibold"
						>{{ $filters.formatCurrency(total, restaurantCurrency) }}
					</span>
				</div>
			</div>
		</div>
	</app-section>
</template>

<script>
	import RestaurantService from '@/services/sales/BusinessService';
	import { mapGetters } from 'vuex';
	import { debounce } from 'lodash';

	export default {
		name: 'BasketSection',
		emits: ['removeFromBasket'],
		props: {
			items: {
				type: Array,
				default: () => [],
			},
			subtotal: {
				type: Number,
				default: 0,
			},
			discountValue: {
				type: Number,
				default: 0,
			},
			transportCost: {
				type: Number,
				default: 0,
			},
			restaurant: {
				type: Number,
				default: 0,
			},
			serviceFee: {
				type: Number,
				default: 0,
			},
			restaurantCurrency: {
				type: Object,
				default: () => ({
					id: 1,
					code: 'ALL',
					description: 'Albanian Lek',
					symbol: 'L',
					decimalPlaces: 0,
					isSymbolAfterAmount: 1,
				}),
			},
		},
		computed: {
			...mapGetters({
				user: 'user',
			}),
			total() {
				return this.subtotal + this.transportCost - this.discountValue + this.localServiceFee;
			},
		},
		data() {
			return {
				localServiceFee: this.serviceFee,
				debouncedGetServiceFee: null,
				serviceFeeLoading: false,
			};
		},
		created() {
			this.debouncedGetServiceFee = debounce(this.getServiceFee, 500);
		},
		methods: {
			getServiceFee() {
				const restaurant = this.user.restaurants.find((r) => r.restaurantID === this.restaurant);
				if (!restaurant.baboonCourier) {
					this.localServiceFee = 0;
					return;
				}
				let payload = {
					total: this.subtotal,
					zone: {
						transportcost: this.transportCost,
					},
					business: {
						restaurantID: restaurant?.restaurantID,
						city: {
							id: restaurant?.city?.id,
						},
					},
				};
				this.serviceFeeLoading = true;
				RestaurantService.getServiceFee(payload)
					.then((serviceFee) => {
						this.localServiceFee = serviceFee;
					})
					.catch((error) => {
						this.$notificationService.error(error);
					})
					.finally(() => {
						this.serviceFeeLoading = false;
					});
			},
		},
		watch: {
			subtotal() {
				this.debouncedGetServiceFee();
			},
		},
	};
</script>

<style lang="scss" scoped>
	.items-container {
		@apply overflow-y-auto;
		max-height: calc(100vh - 320px);
	}
</style>
